import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomModal from "../widgets/CustomModal";
import InfiniteScroll from "../services/InfiniteScroll";
import { HeadingContext } from "../context/HeadingContext";
import AppContext from "../context/AppContext";
import { trainingVideoList } from "../_slice/guideSlice";

// Resolve a media path to a full URL (leave absolute URLs untouched).
// Training video assets are stored on S3 (REACT_APP_MEDIA_URL_ORG).
const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_MEDIA_URL_ORG || "";
  return `${base}${base.endsWith("/") || url.startsWith("/") ? "" : "/"}${url}`;
};

// Pull the video source from any of the likely field names
const getVideoUrl = (item) =>
  resolveMediaUrl(
    item?.video_url || item?.url || item?.video || item?.file || ""
  );

// Pull the thumbnail from any of the likely field names
const getThumbnail = (item) =>
  resolveMediaUrl(
    item?.thumbnail || item?.thumbnail_url || item?.thumb || item?.image || ""
  );

function Guide() {
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [videoModal, setVideoModal] = useState({ show: false, video: null });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);
  const { setHeading } = useContext(HeadingContext);

  // Redux selector to get guide (training video) data and status
  const { guideData, status: guideStatus } = useSelector(
    (state) => state.guide
  );

  useEffect(() => {
    setHeading("Guide");
  }, [setHeading]);

  // Effect to fetch training videos initially
  useEffect(() => {
    if (guideStatus === "idle") {
      fetchMoreVideos(0);
    }
  }, [guideStatus, dispatch, setLoader]);

  // Function to fetch more training videos
  const fetchMoreVideos = (resetPage) => {
    const pageCount = resetPage > 0 ? resetPage : page;

    dispatch(trainingVideoList({ pageCount, navigate, setLoader }))
      .then((response) => {
        const paginator = response?.payload?.videos;
        if (paginator?.data?.length > 0) {
          if (paginator.current_page && paginator.last_page) {
            setPage(paginator.current_page + 1);
            setHasMore(paginator.current_page < paginator.last_page);
          } else {
            // No pagination meta — fall back to next_page_url if present
            setPage(pageCount + 1);
            setHasMore(!!paginator.next_page_url);
          }
        } else {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching more training videos:", error);
      });
  };

  const rows = guideData?.data || [];

  return (
    <>
      <div className="org">
        <div className="org_header d-flex align-items-center justify-content-between">
          <h1 className="org_header_heading">Guide</h1>
        </div>

        <InfiniteScroll
          fetchMoreData={() => fetchMoreVideos(0)}
          hasMore={hasMore}
          isLoading={guideStatus === "loading"}
          threshold={200}
        >
          <div className="org_table table-responsive table-border-0">
            <table className="table">
              <thead>
                <tr>
                  <td className="org_table_column w-15 zone_table_heading">
                    Preview
                  </td>
                  <td className="org_table_column w-25 zone_table_heading">
                    Title
                  </td>
                  <td className="org_table_column w-40 zone_table_heading">
                    Description
                  </td>
                  <td className="org_table_column w-20 text-center zone_table_heading">
                    Action
                  </td>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((item) => {
                    const thumbnail = getThumbnail(item);
                    const title = item.title || item.name || "-";
                    const description = item.description || item.desc || "";
                    return (
                      <tr key={`Guide_${item.id}`}>
                        <td className="org_table_row w-15 zone_body_text">
                          <div
                            style={{
                              width: "96px",
                              height: "60px",
                              borderRadius: "6px",
                              background: "#E5E7EB",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            {thumbnail ? (
                              <img
                                src={thumbnail}
                                alt={title}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  width: "auto",
                                  height: "auto",
                                  objectFit: "contain",
                                  display: "block",
                                }}
                              />
                            ) : (
                              <span style={{ color: "#6B7280", fontSize: "20px" }}>
                                ▶
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="org_table_row w-25 zone_body_text">
                          {title}
                        </td>
                        <td className="org_table_row w-40 zone_body_text">
                          {description}
                        </td>
                        <td className="org_table_row w-20 zone_body_text">
                          <div className="d-flex align-items-center justify-content-center">
                            <button
                              type="button"
                              className="submit_btn"
                              style={{
                                whiteSpace: "nowrap",
                                padding: "6px 14px",
                                fontSize: "13px",
                              }}
                              onClick={() =>
                                setVideoModal({ show: true, video: item })
                              }
                            >
                              Watch
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="org_table_row zone_body_text text-center"
                      style={{ padding: "30px", color: "#6B7280" }}
                    >
                      {guideStatus === "loading"
                        ? "Loading..."
                        : "No training videos found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </InfiniteScroll>
      </div>

      {/* Video Player Modal */}
      <CustomModal
        show={videoModal.show}
        onHide={() => setVideoModal({ show: false, video: null })}
        heading={
          videoModal.video?.title || videoModal.video?.name || "Training Video"
        }
        size="lg"
        scrollable
        body={
          <div>
            {getVideoUrl(videoModal.video) ? (
              <div
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  background: "#000",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <video
                  src={getVideoUrl(videoModal.video)}
                  controls
                  autoPlay
                  style={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>
            ) : (
              <p className="text-center mb-0" style={{ color: "#6B7280" }}>
                Video is not available.
              </p>
            )}
            {videoModal.video?.description && (
              <p
                className="mt-3 mb-0"
                style={{ color: "#292929", fontSize: "14px", lineHeight: "1.6" }}
              >
                {videoModal.video.description}
              </p>
            )}
          </div>
        }
      />
    </>
  );
}

export default Guide;
